import { ReservedValues } from "@/constants/reservedValues";
import type { SceneLayerApi } from "@/layers/SceneLayerApi";
import shaderSources from "@/layers/shaders/all";

export type BinaryMinMax = { min: number; max: number };

export function computeMinMax(payload: Float32Array): BinaryMinMax {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < payload.length; i++) {
    const v = payload[i];
    if (!Number.isFinite(v)) {
      continue;
    }
    if (ReservedValues.includes(v)) {
      continue;
    }
    min = Math.min(min, v);
    max = Math.max(max, v);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1 };
  }
  if (min === max) {
    return { min: min - 1, max: max + 1 };
  }
  return { min, max };
}

export class BinaryDebugRenderer {
  private program?: WebGLProgram;
  private posBuffer?: WebGLBuffer;
  private uvBuffer?: WebGLBuffer;
  private texture?: WebGLTexture;
  private uSampler?: WebGLUniformLocation;
  private uMin?: WebGLUniformLocation;
  private uMax?: WebGLUniformLocation;
  private uOpacity?: WebGLUniformLocation;
  private aPos = -1;
  private aUv = -1;

  private debugTileCacheId?: string;
  private debugMinMax?: BinaryMinMax;

  init(gl: WebGL2RenderingContext): void {
    const extFloat = gl.getExtension("EXT_color_buffer_float");
    if (!extFloat) {
      return;
    }

    const vsSource = shaderSources("./binary/debug.vert").default;
    const fsSource = shaderSources("./binary/debug.frag").default;

    //TODO: remove lot of webgl boilerplate with https://twgljs.org/
    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type);
      if (!sh) {
        throw new Error("Failed to create shader");
      }
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(sh) || "";
        gl.deleteShader(sh);
        throw new Error(log);
      }
      return sh;
    };

    const vs = compile(gl.VERTEX_SHADER, vsSource);
    const fs = compile(gl.FRAGMENT_SHADER, fsSource);

    const program = gl.createProgram();
    if (!program) {
      throw new Error("Failed to create program");
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) || "";
      gl.deleteProgram(program);
      throw new Error(log);
    }

    this.program = program;

    this.aPos = gl.getAttribLocation(program, "a_position");
    this.aUv = gl.getAttribLocation(program, "a_texCoord");
    this.uSampler = gl.getUniformLocation(program, "u_sampler") || undefined;
    this.uMin = gl.getUniformLocation(program, "u_min") || undefined;
    this.uMax = gl.getUniformLocation(program, "u_max") || undefined;
    this.uOpacity = gl.getUniformLocation(program, "u_opacity") || undefined;

    const pos = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    const uv = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);

    this.posBuffer = gl.createBuffer() || undefined;
    if (this.posBuffer) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, pos, gl.STATIC_DRAW);
    }

    this.uvBuffer = gl.createBuffer() || undefined;
    if (this.uvBuffer) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, uv, gl.STATIC_DRAW);
    }

    this.texture = gl.createTexture() || undefined;
    if (this.texture) {
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.bindTexture(gl.TEXTURE_2D, null);
    }
  }

  dispose(gl: WebGL2RenderingContext): void {
    if (this.program) {
      gl.deleteProgram(this.program);
    }
    if (this.posBuffer) {
      gl.deleteBuffer(this.posBuffer);
    }
    if (this.uvBuffer) {
      gl.deleteBuffer(this.uvBuffer);
    }
    if (this.texture) {
      gl.deleteTexture(this.texture);
    }
    this.program = undefined;
    this.posBuffer = undefined;
    this.uvBuffer = undefined;
    this.texture = undefined;
    this.debugTileCacheId = undefined;
    this.debugMinMax = undefined;
  }

  getDebugMinMax(): BinaryMinMax | undefined {
    return this.debugMinMax;
  }

  updateDebugTextureIfNeeded(gl: WebGL2RenderingContext | undefined, scene: SceneLayerApi, lastPvs: unknown): void {
    if (!gl || !this.texture) {
      return;
    }

    const picked = this.pickOneLoadedTileFromPvs(lastPvs);
    if (!picked) {
      return;
    }

    if (this.debugTileCacheId === picked.cacheId) {
      return;
    }

    this.debugTileCacheId = picked.cacheId;
    this.debugMinMax = computeMinMax(picked.payload);

    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    const internalFormat = gl.R32F;
    const srcFormat = gl.RED;
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      internalFormat,
      picked.width,
      picked.height,
      0,
      srcFormat,
      gl.FLOAT,
      picked.payload,
    );

    gl.bindTexture(gl.TEXTURE_2D, null);

    scene.repaint();
  }

  private pickOneLoadedTileFromPvs(
    lastPvs: any,
  ): { cacheId: string; payload: Float32Array; width: number; height: number } | null {
    if (!lastPvs) {
      return null;
    }

    const tilesByZoom: Map<number, any> = lastPvs.get?.() ?? new Map();
    if (!tilesByZoom || tilesByZoom.size === 0) {
      return null;
    }

    const zooms = Array.from(tilesByZoom.keys()).sort((a, b) => b - a);
    for (const z of zooms) {
      const record = tilesByZoom.get(z);
      if (!record) {
        continue;
      }
      const positioned = Object.values(record)[0] as any;
      if (!positioned || positioned.isFailedTile) {
        continue;
      }

      const tile: any = positioned.tile ?? positioned.tileData ?? positioned.val ?? positioned.data ?? positioned;
      const payload: Float32Array | undefined = tile?.payload ?? tile?.data?.payload;
      const width: number | undefined = tile?.width ?? tile?.data?.width;
      const height: number | undefined = tile?.height ?? tile?.data?.height;

      if (!payload || !width || !height) {
        continue;
      }

      const cacheId = `${z}|${
        positioned.tileGeometry?.toId?.() ?? positioned.tileGeometry?.toHumanReadableId?.() ?? "tile"
      }`;
      return { cacheId, payload, width, height };
    }

    return null;
  }
}
